import { collection, doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { getCloudRunEndpoint } from '../config/cloudRunConfig';

export interface ExecuteDeepReasoningJobResponse {
  requestId: string;
  drsJobRequestId: string;
  executionName?: string;
  runId: string;
  manifestId: string;
}

export interface BatchRunData {
  status?: string;
  totalPages?: number;
  processedPages?: number;
  successPages?: number;
  failedPages?: number;
  skippedPages?: number;
  totalSuggestions?: number;
  // ページ完了ごとに job 側で serverTimestamp() が書かれる Firestore Timestamp。
  // 停滞検知のソースとして使う（toMillis() で ms epoch を取得）。
  updatedAt?: any;
}

export type BatchPageStatus = 'processing' | 'done' | 'skipped' | 'failed';

export interface BatchPageData {
  pageId: string;
  status?: BatchPageStatus | string;
  taskIndex?: number;
  suggestionsCount?: number;
  attempts?: number;
  languageUsed?: string;
  reason?: string;
  error?: string;
  updatedAt?: any;
}

export interface BatchPagesAggregation {
  processing: number;
  done: number;
  skipped: number;
  failed: number;
  total: number;
}

export class DeepReasoningApiError extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly responseBody: string;

  constructor(status: number, statusText: string, responseBody: string) {
    const classification =
      status === 401
        ? 'unauthenticated'
        : status === 403
          ? 'permission-denied'
          : 'internal';
    super(
      `deepReasoning job execute failed (${classification}): ${status} ${statusText}${responseBody ? ` - ${responseBody}` : ''}`
    );
    this.name = 'DeepReasoningApiError';
    this.status = status;
    this.statusText = statusText;
    this.responseBody = responseBody;
  }
}

export async function executeDeepReasoningJob(params: {
  workspaceId: string;
  pageIds: string[];
  languageCode?: string;
  taskInnerConcurrency?: number;
}): Promise<ExecuteDeepReasoningJobResponse> {
  const baseUrl = getCloudRunEndpoint('deepReasoning');
  const url = `${baseUrl}/execute-suggest-job`;
  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) {
    throw new DeepReasoningApiError(401, 'Unauthorized', 'Missing Firebase ID token');
  }

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(params),
  });

  const text = await res.text().catch(() => '');
  if (!res.ok) {
    throw new DeepReasoningApiError(res.status, res.statusText, text);
  }
  return (text ? JSON.parse(text) : {}) as ExecuteDeepReasoningJobResponse;
}

export interface CancelDeepReasoningRunResponse {
  requestId: string;
  runId: string;
  cancelled: boolean;
  executionCancelOk?: boolean;
  executionCancelStatus?: number | null;
  // 既に終了済みの run に対して cancel を呼んだ場合に true。
  // このとき finalStatus に現在の status が入る (cancelled / completed / completed_with_issues / failed)。
  alreadyFinished?: boolean;
  finalStatus?: string;
}

/**
 * 進行中の DRS バッチを強制終了する。
 * - batchRuns/{runId}.status を 'cancelled' に更新
 * - drsJobRequests/{runId}.cancelled = true
 * - Cloud Run Jobs Executions API: cancel を呼び Job 自体も停止する（best-effort）
 */
export async function cancelDeepReasoningRun(params: {
  workspaceId: string;
  runId: string;
  reason?: string;
}): Promise<CancelDeepReasoningRunResponse> {
  const baseUrl = getCloudRunEndpoint('deepReasoning');
  const url = `${baseUrl}/cancel-run`;
  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) {
    throw new DeepReasoningApiError(401, 'Unauthorized', 'Missing Firebase ID token');
  }

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(params),
  });

  const text = await res.text().catch(() => '');
  if (!res.ok) {
    throw new DeepReasoningApiError(res.status, res.statusText, text);
  }
  return (text ? JSON.parse(text) : {}) as CancelDeepReasoningRunResponse;
}

// ==================================================================
// errors コレクション連携（フロント側）
// Hosting rewrites 未デプロイのため、直 URL を叩く運用とする。
// 失敗しても元の処理は止めない（fire-and-forget）。
// ==================================================================
const LOG_ERROR_URL = 'https://us-central1-eq-board.cloudfunctions.net/logError';

export interface LogFrontendErrorParams {
  code: string;
  severity: 'info' | 'warn' | 'error' | 'fatal';
  message: string;
  workspaceId?: string;
  runId?: string;
  extra?: Record<string, unknown>;
  stack?: string;
}

export async function logFrontendError(params: LogFrontendErrorParams): Promise<void> {
  try {
    const { code, severity, message, workspaceId, runId, extra, stack } = params;
    const body = {
      source: 'app',
      code,
      message: String(message).slice(0, 2000),
      severity,
      context: {
        workspaceId: workspaceId || undefined,
        sessionId: runId || undefined,
        pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        extra: {
          runId,
          ...(extra || {}),
        },
      },
      stack,
      occurredAt: Date.now(),
    };
    await fetch(LOG_ERROR_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (e) {
    console.warn('[deepReasoningJob] logFrontendError failed (ignored):', e);
  }
}

export function subscribeDeepReasoningBatchRun(params: {
  workspaceId: string;
  runId: string;
  onChange: (data: BatchRunData | null) => void;
  onError?: (e: unknown) => void;
}): () => void {
  const ref = doc(db, 'workspaces', params.workspaceId, 'batchRuns', params.runId);
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        params.onChange(null);
        return;
      }
      params.onChange(snap.data() as any);
    },
    (e) => {
      params.onError?.(e);
    }
  );
}

/**
 * batchRuns/{runId}/pages サブコレクションを購読し、status ごとの件数を集計して通知する。
 * UI ではページ一覧は表示せず件数のみ使うため、aggregation 形式で返す。
 */
export function subscribeDeepReasoningBatchPages(params: {
  workspaceId: string;
  runId: string;
  onChange: (aggregation: BatchPagesAggregation) => void;
  onError?: (e: unknown) => void;
}): () => void {
  const ref = collection(db, 'workspaces', params.workspaceId, 'batchRuns', params.runId, 'pages');
  return onSnapshot(
    ref,
    (snap) => {
      const agg: BatchPagesAggregation = {
        processing: 0,
        done: 0,
        skipped: 0,
        failed: 0,
        total: snap.size,
      };
      snap.forEach((d) => {
        const status = String((d.data() as BatchPageData)?.status || 'processing');
        if (status === 'done') agg.done += 1;
        else if (status === 'skipped') agg.skipped += 1;
        else if (status === 'failed') agg.failed += 1;
        else agg.processing += 1;
      });
      params.onChange(agg);
    },
    (e) => {
      params.onError?.(e);
    }
  );
}

export async function waitForDeepReasoningBatchRunCompletion(params: {
  workspaceId: string;
  runId: string;
  onProgress?: (data: BatchRunData) => void;
}): Promise<BatchRunData> {
  return await new Promise<BatchRunData>((resolve, reject) => {
    const unsub = subscribeDeepReasoningBatchRun({
      workspaceId: params.workspaceId,
      runId: params.runId,
      onChange: (data) => {
        if (!data) return;
        params.onProgress?.(data);
        const st = String(data.status || '');
        if (st === 'completed' || st === 'completed_with_issues') {
          unsub();
          resolve(data);
        }
      },
      onError: (e) => {
        unsub();
        reject(e);
      },
    });
  });
}

